import { useLazyQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const QUERY_FIELD_SETTINGS_BY_MODEL = gql`
  query SP_QueryFieldSettingByModel($topLandlordId: ID!, $model: CustomFieldModelTypeOptions!) {
    getFieldSettingsByModel(topLandlordId: $topLandlordId, model: $model) {
      id
      modelType
      fieldFormat
      type
      labelName
      apiFieldName
      picklistOptions {
        name
        value
      }
      fieldName
      inputTypeInForm
    }
  }
`;

const usePersonDropdownOptions = ({ variables }) => {
  const { t } = useTranslation(['student', 'graphql']);

  const [queryPersonDropdownOptions, { data }] = useLazyQuery(QUERY_FIELD_SETTINGS_BY_MODEL, {
    variables,
  });

  const personDropdownOptions = data?.getFieldSettingsByModel;

  const titleOptions = useMemo(() => {
    const picklistOptions = personDropdownOptions?.find(
      (item) => item?.fieldName?.toLowerCase() === 'title'
    )?.picklistOptions;

    return (
      picklistOptions?.map((option) => ({
        label: option?.name,
        value: option?.value,
      })) || []
    );
  }, [personDropdownOptions]);

  const heardSourceOptions = useMemo(() => {
    const picklistOptions = personDropdownOptions?.find(
      (item) => item?.fieldName?.toLowerCase() === 'heard_source'
    )?.picklistOptions;

    return (
      picklistOptions?.map((option) => ({
        label: option?.name,
        value: option?.value,
      })) || []
    );
  }, [personDropdownOptions]);

  // //Degree Classification
  const degreeClassificationOptions = useMemo(() => {
    const picklistOptions = personDropdownOptions?.find(
      (item) => item?.fieldName?.toLowerCase() === 'degree_classification'
    )?.picklistOptions;

    return (
      picklistOptions?.map((option) => ({
        children: t(`graphql:degree_classification.${option?.value?.toUpperCase()}`),
        value: option?.value,
      })) || []
    );
  }, [personDropdownOptions, t]);

  const yearOfStudyOptions = useMemo(() => {
    const picklistOptions = personDropdownOptions?.find(
      (item) => item?.fieldName?.toLowerCase() === 'year_of_study'
    )?.picklistOptions;

    return (
      picklistOptions?.map((option) => ({
        children: t(`graphql:year_of_study_type.${option?.value?.toUpperCase()}`),
        value: option?.value,
      })) || []
    );
  }, [personDropdownOptions, t]);

  const genderPronounOptions = useMemo(() => {
    const picklistOptions = personDropdownOptions?.find(
      (item) => item?.fieldName?.toLowerCase() === 'preferred_gender_pronoun'
    )?.picklistOptions;

    const pronounOptions =
      picklistOptions?.map((option) => ({
        label: t(`pronoun.option.${option?.value?.toUpperCase()}`),
        value: option?.value,
      })) || [];
    return [
      ...pronounOptions,
      {
        value: '',
        label: t(`pronoun.option.NONE`),
      },
    ];
  }, [personDropdownOptions, t]);

  const topLandlordId = variables?.topLandlordId;
  const model = variables?.model;

  useEffect(() => {
    if (model && topLandlordId) {
      queryPersonDropdownOptions();
    }
  }, [model, queryPersonDropdownOptions, topLandlordId]);

  return {
    titleOptions,
    heardSourceOptions,
    yearOfStudyOptions,
    degreeClassificationOptions,
    genderPronounOptions,
  };
};

export { usePersonDropdownOptions };
